import {
  Box,
  Typography,
  Grid,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Table,
  TableBody,
  Divider,
  Badge,
  Container,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  DialogContent,
  Dialog,
  DialogTitle,
  IconButton,
  Tooltip,
  Button,
  TextField,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Loading } from "../../components/Loading";
import { financeApi } from "./apiService";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import {
  tableHeaderStyles,
  commonTypographyProps,
  tableCellStyles,
  smallButtonProp,
} from "../../utils/styles";
import {
  formatCurrency,
  formatDate,
  formatDateRange,
  formatText,
  getDateRangeForFilter,
  TimeFilter,
  TimeFilterWithNone,
} from "../../utils/utils";
import { useLocation, useParams } from "react-router-dom";
import NavigationHeader from "../../components/NavigationHeader";
import AddIcon from "@mui/icons-material/Add";
import IncomePaymentModal from "./components/IncomePaymentModal";
import CloseIcon from "@mui/icons-material/Close";
import { tooltipInfo } from "./IncomePartyDetails";
import { Info } from "@mui/icons-material";
import { PaymentModeFormatter } from "./Transactions";

// Reusable Date Filter Component
export const DateFilterComponent = ({
  timeFilter,
  setTimeFilter,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  onApplyFilter,
  onClearFilters,
}: {
  timeFilter: TimeFilterWithNone;
  setTimeFilter: (filter: TimeFilterWithNone) => void;
  customStartDate: string;
  setCustomStartDate: (date: string) => void;
  customEndDate: string;
  setCustomEndDate: (date: string) => void;
  onApplyFilter: () => void;
  onClearFilters: () => void;
}) => {
  const handleDateFilterChange = (newTimeFilter: TimeFilterWithNone) => {
    setTimeFilter(newTimeFilter);
    if (newTimeFilter === "custom") {
      return;
    }
    setCustomStartDate("");
    setCustomEndDate("");
  };

  return (
    <Paper sx={{ p: 1, mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: "150px" },
            }}
          >
            <FormControl fullWidth size="small" sx={{ fontSize: "0.65rem" }}>
              <Select
                value={timeFilter}
                onChange={(e) =>
                  handleDateFilterChange(e.target.value as TimeFilterWithNone)
                }
                displayEmpty
                sx={{ fontSize: "0.65rem" }}
              >
                <MenuItem value="" sx={{ fontSize: "0.65rem" }} disabled>
                  Select Date
                </MenuItem>
                <MenuItem value="today" sx={{ fontSize: "0.65rem" }}>
                  Today ({formatDate(getDateRangeForFilter("today").start)})
                </MenuItem>
                <MenuItem value="thisWeek" sx={{ fontSize: "0.65rem" }}>
                  This Week{" "}
                  {formatDateRange(
                    getDateRangeForFilter("thisWeek").start,
                    getDateRangeForFilter("thisWeek").end
                  )}
                </MenuItem>
                <MenuItem value="last7days" sx={{ fontSize: "0.65rem" }}>
                  Last 7 Days{" "}
                  {formatDateRange(
                    getDateRangeForFilter("last7days").start,
                    getDateRangeForFilter("last7days").end
                  )}
                </MenuItem>
                <MenuItem value="lastWeek" sx={{ fontSize: "0.65rem" }}>
                  Last Week{" "}
                  {formatDateRange(
                    getDateRangeForFilter("lastWeek").start,
                    getDateRangeForFilter("lastWeek").end
                  )}
                </MenuItem>
                <MenuItem value="thisMonth" sx={{ fontSize: "0.65rem" }}>
                  This Month{" "}
                  {formatDateRange(
                    getDateRangeForFilter("thisMonth").start,
                    getDateRangeForFilter("thisMonth").end
                  )}
                </MenuItem>
                <MenuItem value="lastMonth" sx={{ fontSize: "0.65rem" }}>
                  Last Month{" "}
                  {formatDateRange(
                    getDateRangeForFilter("lastMonth").start,
                    getDateRangeForFilter("lastMonth").end
                  )}
                </MenuItem>
                <MenuItem value="financialYear" sx={{ fontSize: "0.65rem" }}>
                  Financial Year{" "}
                  {formatDateRange(
                    getDateRangeForFilter("financialYear").start,
                    getDateRangeForFilter("financialYear").end
                  )}
                </MenuItem>
                <MenuItem value="custom" sx={{ fontSize: "0.65rem" }}>
                  Custom Range
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {timeFilter === "custom" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "150px" },
                }}
              >
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                  inputProps={{
                    style: {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: { xs: "100%", sm: "auto" },
                  minWidth: { sm: "150px" },
                }}
              >
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                    },
                  }}
                  inputProps={{
                    style: {
                      fontSize: "0.75rem",
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexShrink: 0,
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-end", sm: "flex-start" },
            mt: { xs: 1, sm: 0 },
          }}
        >
          {timeFilter && (
            <Button
              variant="contained"
              onClick={onApplyFilter}
              disabled={
                timeFilter === "custom" && (!customStartDate || !customEndDate)
              }
              {...smallButtonProp}
            >
              <span style={{ fontSize: "0.65rem" }}>Apply</span>
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={onClearFilters}
            {...smallButtonProp}
          >
            <span style={{ fontSize: "0.65rem" }}>Clear Filters</span>
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

const getTransactionConfig = (transaction: any) => {
  let label = transaction?.transactionType;
  let color = "info";
  if (transaction?.transactionType === "Bus Commission Booking") {
    color =
      transaction?.amount > 0
        ? "success"
        : transaction?.amount < 0
        ? "error"
        : "info";
    label =
      transaction?.amount > 0
        ? "To be received"
        : transaction?.amount < 0
        ? "To be paid"
        : "No due";
  } else if (transaction?.transactionType === "Commission Booking") {
    color =
      transaction?.amount > 0
        ? "error"
        : transaction?.amount === 0
        ? "info"
        : "success";
    label =
      transaction?.amount > 0
        ? "To be paid"
        : transaction?.amount < 0
        ? "To be received"
        : "No due";
  } else if (
    transaction?.transactionType === "Contract Booking" ||
    transaction?.transactionType === "Booking"
  ) {
    color = "success";
    label = "To be received";
  } else {
    color = [
      "Collection",
      "Received",
      "Advance On Booking",
      "Payment On Booking",
    ].includes(transaction?.transactionType)
      ? "success"
      : ["Debit", "Settlement", "Advance", "Expense", "Paid"].includes(
          transaction?.transactionType
        )
      ? "error"
      : "info";
    label = transaction?.transactionType;
  }
  return {
    color,
    label,
  };
};

const IncomeTransactions = () => {
  const { partyId } = useParams();
  const location = useLocation() as any;
  const { party, partyType } = location.state || {};
  const { planStatus, loading } = usePlanStatus();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Transaction Modal State
  const [selectedParty, setSelectedParty] = useState<any>(party);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionLoading, setTransactionLoading] = useState(false);

  // Date filter state
  const [timeFilter, setTimeFilter] = useState<TimeFilterWithNone>("");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Pagination state
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState<any>({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 20,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [summary, setSummary] = useState<any>({
    totalBooking: 0,
    netReceived: 0,
    netReceivable: 0,
    totalBalance: 0,
    totalTransactions: 0,
    totalAdvance: 0,
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (partyId && !loading) {
      setTransactions([]);
      fetchTransactions(partyId, partyType, 1);
    } else if (!partyId) {
      setTransactions([]);
    }
  }, [partyId, planStatus, loading]);

  const fetchTransactions = async (
    partyId: string,
    partyType: string,
    page: number,
    limitOverride?: number,
    applyFilter: boolean = true
  ) => {
    if (!planStatus?.company?.id || loading) return;

    setTransactionLoading(true);

    try {
      // Only apply date filter if user has selected a specific filter
      let startDate = null;
      let endDate = null;

      if (timeFilter && applyFilter) {
        if (timeFilter === "custom" && customStartDate && customEndDate) {
          startDate = customStartDate;
          endDate = customEndDate;
        } else if (timeFilter !== "custom") {
          const dateRange = getDateRangeForFilter(timeFilter);
          // Convert to YYYY-MM-DD format for backend using local timezone
          startDate = dateRange.start.toLocaleDateString("en-CA"); // en-CA gives YYYY-MM-DD format
          endDate = dateRange.end.toLocaleDateString("en-CA");

          // Debug logging
          console.log(`Date filter "${timeFilter}":`, {
            start: dateRange.start.toLocaleDateString("en-IN"), // Indian format for display
            end: dateRange.end.toLocaleDateString("en-IN"),
            startDate, // API format
            endDate, // API format
          });
        }
      }

      // For empty filter, don't send any date parameters to backend

      const response = await financeApi.getTransactionsByPartyId(
        partyType,
        partyId,
        "income",
        page,
        limitOverride ?? pageSize,
        startDate,
        endDate
      );

      const newTransactions = response.transactions || [];
      const paginationData = response.pagination || {};
      const summary = response.summary;

      setTransactions(newTransactions);
      setSummary(summary);
      setPagination({
        totalRecords: paginationData.totalRecords || 0,
        totalPages: paginationData.totalPages || 1,
        currentPage: paginationData.currentPage || page,
        limit: paginationData.limit || limitOverride || pageSize,
        hasNextPage: Boolean(paginationData.hasNextPage),
        hasPreviousPage: Boolean(paginationData.hasPreviousPage),
      });
      if (paginationData.limit) setPageSize(paginationData.limit);
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setTransactionLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    if (partyId) {
      fetchTransactions(partyId, partyType, value, pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    const newPageSize = event.target.value as number;
    setPageSize(newPageSize);
    if (partyId) {
      fetchTransactions(partyId, partyType, 1, newPageSize);
    }
  };

  // Handle applying the selected filter
  const handleApplyFilter = () => {
    if (timeFilter && partyId) {
      fetchTransactions(partyId, partyType, 1, pageSize);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setTimeFilter("");
    setCustomStartDate("");
    setCustomEndDate("");
    // Load all transactions when clearing filters
    if (partyId) {
      fetchTransactions(partyId, partyType, 1, pageSize, false);
    }
  };

  if (!partyId) return null;

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        breadcrumbs={[
          { label: "Finance", path: "/finance" },
          { label: "Receivables", path: "/finance/income" },
          {
            label: partyType ? formatText(partyType) : "Party Details",
            path: `/finance/income/party/${partyType}`,
          },
          { label: "Transactions" },
        ]}
        title={selectedParty?.name}
        button={{
          label: "Payment",
          onClick: () => {
            setSelectedParty(party);
            setPaymentModalOpen(true);
          },
          icon: <AddIcon />,
        }}
      />

      {/* Date Filter Section */}
      <DateFilterComponent
        timeFilter={timeFilter}
        setTimeFilter={setTimeFilter}
        customStartDate={customStartDate}
        setCustomStartDate={setCustomStartDate}
        customEndDate={customEndDate}
        setCustomEndDate={setCustomEndDate}
        onApplyFilter={handleApplyFilter}
        onClearFilters={handleClearFilters}
      />

      <Paper sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          {/* Party Summary */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2}>
              <Tooltip title={tooltipInfo.totalBooking} placement="top">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" sx={commonTypographyProps}>
                    Booking
                  </Typography>
                  <Info sx={{ fontSize: "0.7rem", ml: 0.5 }} color="primary" />
                </Box>
              </Tooltip>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.totalBooking || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Tooltip title={tooltipInfo.netReceivable} placement="top">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" sx={commonTypographyProps}>
                    Receivable
                  </Typography>
                  <Info sx={{ fontSize: "0.7rem", ml: 0.5 }} color="primary" />
                </Box>
              </Tooltip>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.netReceivable || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Tooltip title={tooltipInfo.netReceived} placement="top">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" sx={commonTypographyProps}>
                    Received
                  </Typography>
                  <Info sx={{ fontSize: "0.7rem", ml: 0.5 }} color="primary" />
                </Box>
              </Tooltip>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.netReceived || 0)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Tooltip title={tooltipInfo.totalBalance} placement="top">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="h6" sx={commonTypographyProps}>
                    Balance
                  </Typography>
                  <Info sx={{ fontSize: "0.7rem", ml: 0.5 }} color="primary" />
                </Box>
              </Tooltip>
              <Typography variant="body1" sx={commonTypographyProps}>
                {formatCurrency(summary?.totalBalance || 0)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          {/* Transactions Table */}
          {transactionLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Loading message="Loading transactions..." />
            </Box>
          ) : (
            <>
              <IncomeTransactionTable
                transactions={transactions}
                transactionLoading={transactionLoading}
                timeFilter={timeFilter}
              />
              {/* Pagination Controls */}
              {pagination && pagination.totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Rows per page:
                    </Typography>
                    <FormControl
                      size="small"
                      sx={{ minWidth: 80, fontSize: "0.75rem" }}
                    >
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        displayEmpty
                        size="small"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        <MenuItem value={20} sx={{ fontSize: "0.75rem" }}>
                          20
                        </MenuItem>
                        <MenuItem value={50} sx={{ fontSize: "0.75rem" }}>
                          50
                        </MenuItem>
                        <MenuItem value={100} sx={{ fontSize: "0.75rem" }}>
                          100
                        </MenuItem>
                        <MenuItem value={200} sx={{ fontSize: "0.75rem" }}>
                          200
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="body2" color="text.secondary">
                      {`${
                        (pagination.currentPage - 1) *
                          (pagination.limit || pageSize) +
                        1
                      }-${Math.min(
                        pagination.currentPage * (pagination.limit || pageSize),
                        pagination.totalRecords
                      )} of ${pagination.totalRecords}`}
                    </Typography>
                  </Box>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Paper>
      <IncomePaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        selectedParty={selectedParty}
        onTransactionAdded={() => {
          fetchTransactions(partyId, partyType, 1);
          setPaymentModalOpen(false);
        }}
        partyType={partyType}
      />
    </Container>
  );
};

export default IncomeTransactions;

const TransactionDetails = ({ transaction }: { transaction: any }) => {
  const [showMore, setShowMore] = useState(false);

  if (transaction?.transactionType === "Commission Booking") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ fontSize: "0.75rem" }}
        >
          {transaction?.transactionDetails || transaction?.description || "-"}
        </Typography>
        {transaction?.operatorRole === "pickupOperator" && (
          <>
            {transaction?.paidCollectedBy === "Operator" && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6rem" }}
              >
                Paid: {formatCurrency(transaction?.paid || 0)}, collected by
                operator
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.6rem" }}
            >
              Operator Commission:{" "}
              {formatCurrency(transaction?.operatorCommission || 0)}
            </Typography>
          </>
        )}
        {transaction?.operatorRole === "dropOperator" &&
          transaction?.toPayCollectedBy === "Operator" && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.6rem" }}
              >
                To Pay: {formatCurrency(transaction?.toPay || 0)}, collected by
                operator
              </Typography>
            </>
          )}
        {!showMore && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.6rem",
              cursor: "pointer",
              color: "primary.main",
              textDecoration: "underline",
              fontStyle: "italic",
            }}
            onClick={() => setShowMore(true)}
          >
            Show booking details
          </Typography>
        )}
        <Dialog
          open={showMore}
          onClose={() => setShowMore(false)}
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 320,
            },
          }}
        >
          <DialogTitle
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
              Booking Details
            </Typography>
            <IconButton
              onClick={() => setShowMore(false)}
              size="small"
              sx={{ p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  LS No:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.7rem", fontWeight: 600 }}
                >
                  {transaction?.lSNo || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                >
                  LR No:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.7rem", fontWeight: 600 }}
                >
                  {transaction?.lrNo || "-"}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Booking Amount:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                >
                  {formatCurrency(transaction?.bookingAmount || 0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Operator Commission:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                >
                  {formatCurrency(transaction?.operatorCommission || 0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    Paid:
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.65rem",
                      fontStyle: "italic",
                      color: "grey.500",
                    }}
                  >
                    collected by{" "}
                    {transaction?.paidCollectedBy === "Operator"
                      ? "operator"
                      : "our company"}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                >
                  {formatCurrency(transaction?.paid || 0)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem", fontWeight: 500 }}
                  >
                    To Pay:
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontSize: "0.65rem",
                      fontStyle: "italic",
                      color: "grey.500",
                    }}
                  >
                    collected by{" "}
                    {transaction?.toPayCollectedBy === "Operator"
                      ? "operator"
                      : "our company"}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.75rem", fontWeight: 600 }}
                >
                  {formatCurrency(transaction?.toPay || 0)}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </Box>
    );
  } else if (transaction?.transactionType === "Bus Commission Booking") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ fontSize: "0.75rem" }}
        >
          {transaction?.transactionDetails || transaction?.description || "-"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.6rem" }}
        >
          Total: {formatCurrency(transaction?.total || 0)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.6rem" }}
        >
          Operator Role: {transaction?.operatorRole || "-"}
        </Typography>
        {transaction?.operatorRole === "Primary Operator" && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            Primary Operator Commission:{" "}
            {formatCurrency(transaction?.operatorCommission || 0)}
          </Typography>
        )}
        {transaction?.operatorRole === "Secondary Operator" && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.6rem" }}
          >
            Secondary Operator Commission:{" "}
            {formatCurrency(transaction?.agentBranchCommission || 0)}
          </Typography>
        )}
      </Box>
    );
  } else if (
    transaction?.transactionType === "Contract Booking" ||
    transaction?.transactionType === "Booking"
  ) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          variant="caption"
          color="text.primary"
          sx={{ fontSize: "0.75rem" }}
        >
          {transaction?.transactionDetails || transaction?.description || "-"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.6rem" }}
        >
          Booking Amount: {formatCurrency(transaction?.amount || 0)}
        </Typography>
      </Box>
    );
  } else if (transaction?.transactionType === "Invoiced Amount") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {transaction?.transactionDetails ? (
          transaction.transactionDetails
            .split("|")
            .map((part: any, index: number) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  color: index === 0 ? "text.primary" : "text.secondary",
                  fontSize: index === 0 ? "0.75rem" : "0.65rem",
                }}
              >
                {part.trim()}
              </Typography>
            ))
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </Box>
    );
  } else {
    return (
      <Typography
        variant="caption"
        color="text.primary"
        sx={{ fontSize: "0.75rem" }}
      >
        {transaction?.transactionDetails || transaction?.description || "-"}
      </Typography>
    );
  }
};

export const IncomeTransactionTable = ({
  transactions,
  transactionLoading,
  timeFilter,
}: {
  transactions: any[];
  transactionLoading?: boolean;
  timeFilter?: TimeFilterWithNone;
}) => {
  return (
    <TableContainer sx={{ mb: 2 }}>
      <Table
        size="small"
        sx={{
          "& .MuiTableCell-root": {
            padding: "4px 8px",
            fontSize: "0.75rem",
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={tableHeaderStyles}>Date</TableCell>
            <TableCell sx={tableHeaderStyles}>Transaction Details</TableCell>
            <TableCell sx={tableHeaderStyles}>Payment Mode</TableCell>
            <TableCell sx={tableHeaderStyles}>Type</TableCell>
            <TableCell sx={tableHeaderStyles}>Final Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions?.map((transaction, index) => {
            const { color, label } = getTransactionConfig(transaction);
            return (
              <TableRow
                key={transaction?.id || index}
                sx={{ verticalAlign: "top" }}
              >
                <TableCell sx={{ ...tableCellStyles, width: "10%" }}>
                  {formatDate(transaction.date)}
                </TableCell>
                <TableCell sx={{ ...tableCellStyles, width: "45%" }}>
                  <TransactionDetails transaction={transaction} />
                </TableCell>
                <TableCell sx={{ ...tableCellStyles, width: "15%" }}>
                  {PaymentModeFormatter[
                    transaction?.paymentMode as keyof typeof PaymentModeFormatter
                  ] || "-"}
                </TableCell>
                <TableCell sx={{ ...tableCellStyles, width: "15%" }}>
                  <Badge
                    badgeContent={label}
                    color={color as any}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.65rem",
                        minHeight: "auto",
                        height: "auto",
                        padding: "4px 8px",
                        borderRadius: "5px",
                        whiteSpace: "nowrap",
                        maxWidth: "none",
                        minWidth: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        lineHeight: 1,
                      },
                    }}
                  />
                </TableCell>
                <TableCell sx={{ ...tableCellStyles, width: "15%" }}>
                  {formatCurrency(Math.abs(transaction?.amount || 0))}
                </TableCell>
              </TableRow>
            );
          })}
          {transactions?.length === 0 && !transactionLoading && timeFilter ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: "center", py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No transactions found for the selected date range. Try
                  adjusting your filter or clear filters to view all
                  transactions.
                </Typography>
              </TableCell>
            </TableRow>
          ) : transactions?.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: "center", py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  No transactions found
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
